import type { EnvironmentId, ProjectId } from "@cmmd-forge/contracts";
import { RefreshCwIcon, SearchIcon } from "lucide-react";

import { Button } from "../../components/ui/button";
import { InputGroup, InputGroupAddon, InputGroupInput } from "../../components/ui/input-group";
import type { PullRequestInboxError } from "./pullRequests.inbox.logic";
import type { PullRequestsSearch } from "./pullRequests.logic";
import { PullRequestsFilterMenu } from "./PullRequestsFilterMenu";
import { RESPONSIVE_ICON_BUTTON_CLASS } from "~/lib/touchTarget";

export interface CapableEnvironment {
  readonly environmentId: EnvironmentId;
  readonly label: string;
}

interface PullRequestsHeaderProps {
  readonly search: PullRequestsSearch;
  readonly hostOptions: ReadonlyArray<string>;
  readonly projectOptions: ReadonlyArray<{ readonly id: ProjectId; readonly label: string }>;
  readonly unavailableErrors: ReadonlyArray<PullRequestInboxError>;
  readonly isRefreshing: boolean;
  readonly onSearchChange: (search: PullRequestsSearch) => void;
  readonly onReset: () => void;
  readonly onRefresh: () => void;
}

export function PullRequestsHeader(props: PullRequestsHeaderProps) {
  const { search } = props;
  return (
    <div className="flex min-w-0 items-center gap-2">
      <InputGroup className="min-w-0 flex-1 **:[input]:h-touch sm:**:[input]:h-touch md:**:[input]:h-8">
        <InputGroupAddon>
          <SearchIcon aria-hidden="true" />
        </InputGroupAddon>
        <InputGroupInput
          type="search"
          aria-label="Search pull requests"
          placeholder="Search pull requests, or label:bug"
          value={search.query ?? ""}
          onChange={(event) => {
            const { query: _query, ...remaining } = search;
            const query = event.target.value.slice(0, 200);
            props.onSearchChange(query.length === 0 ? remaining : { ...remaining, query });
          }}
        />
      </InputGroup>
      <PullRequestsFilterMenu
        search={search}
        hostOptions={props.hostOptions}
        projectOptions={props.projectOptions}
        unavailableErrors={props.unavailableErrors}
        onSearchChange={props.onSearchChange}
        onReset={props.onReset}
      />
      <Button
        size="icon"
        variant="outline"
        className={RESPONSIVE_ICON_BUTTON_CLASS}
        aria-label="Refresh pull requests"
        disabled={props.isRefreshing}
        onClick={props.onRefresh}
      >
        <RefreshCwIcon
          aria-hidden="true"
          className={props.isRefreshing ? "size-4 animate-spin" : "size-4"}
        />
      </Button>
    </div>
  );
}
